/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button } from '@msdyn365-commerce-modules/utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import ControlledDrawer from '../../components/citta-accordion-drawer/Drawer';
import { IDrawerProps } from '../../components/citta-accordion-drawer/Drawer.props';
import { createAccordionStateInput } from '../../data-actions/accordion/accordion-state-data-action';
import { ICittaAccordionItemCustomData } from './citta-accordion-item-custom.data';
import { ICittaAccordionItemCustomProps } from './citta-accordion-item-custom.props.autogenerated';
export interface IAccordionItemState extends React.ComponentState {
    /**
     * Indicates whether a submenu is expanded or collapsed
     */
    expanded: boolean;
    accordionItemTitle: string;
}

/**
 *
 * AccordionItem component
 * @extends {React.Component<IAccordionItemProps<IAccordionItemData>>}
 */
@observer
class AccordionItemCustom extends React.Component<ICittaAccordionItemCustomProps<ICittaAccordionItemCustomData>, IAccordionItemState> {
    private expandSingleItemOnly?: boolean = false;

    constructor(props: ICittaAccordionItemCustomProps<ICittaAccordionItemCustomData>) {
        super(props);
        this._onToggleAccordionItem = this._onToggleAccordionItem.bind(this);
        this._getDrawerProps = this._getDrawerProps.bind(this);
        this._renderAccordionItemContent = this._renderAccordionItemContent.bind(this);
        this._renderAccordionItemSlotsContent = this._renderAccordionItemSlotsContent.bind(this);
        this.state = {
            expanded: false, // isExpanded
            accordionItemTitle: this.props.config.accordionItemTitle
        };
        this.expandSingleItemOnly = this.props.data.accordionExpandedState.result && this.props.data.accordionExpandedState.result.expandSingleItemOnly;
    }

    public componentDidMount(): void {
        const initialExpansion = this.props && this.props.config && this.props.config.isExpandedOnInitialLoad || false;
        if (initialExpansion) {
            this.setState(prevState => ({
                expanded: !prevState.expanded
            }));

            if (this.expandSingleItemOnly) {
                const oldRadioButtonText = this.props.data && this.props.data.accordionExpandedState && this.props.data.accordionExpandedState.result!.radioButtonText;
                const closing = this.state.accordionItemTitle === oldRadioButtonText;
                const accordionState = { radioButtonText: closing ? '' : this.props.config.accordionItemTitle, isAllExpanded: false };
                this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
            }
        }
    }

    public render(): JSX.Element {
        const { slots } = this.props;
        const config = this.props && this.props.config;
        const drawerProps = this._getDrawerProps(config && config.showGlyph);

        const hasAccordionItemSlotsContent = slots && slots.accordionItemContent && slots.accordionItemContent.length;

        return (
            <div>
                {hasAccordionItemSlotsContent && (
                    <div className='accordion-item-custom'>
                        <Button
                            onClick={this._onToggleAccordionItem(this.props.config.accordionItemTitle, true)}
                            className='accordion-item-custom-button'
                        />
                        <div className='accordion-item-title'>
                            {(config) && (
                                <ControlledDrawer {...drawerProps}>
                                   <div className={'accordion-item-content'}>
                                        {hasAccordionItemSlotsContent && (this._renderAccordionItemSlotsContent(slots.accordionItemContent))}
                                    </div>
                                </ControlledDrawer>
                            )
                            }
                        </div>
                    </div>)
                }
            </div>
        );
    }

    private _getDrawerProps(showGlyph: boolean | undefined): IDrawerProps {
        const data = this.props && this.props.data;
        const config = this.props && this.props.config;
        const accordionExpandedStateResult = data && data.accordionExpandedState && data.accordionExpandedState.result;
        // const expanded = accordionExpandedStateResult ? accordionExpandedStateResult!.isAllExpanded === undefined ?
        //     data && config.isExpandedOnInitialLoad : accordionExpandedStateResult!.isAllExpanded : false;
        const expanded = this.state.expanded;
        const radioButtonText = accordionExpandedStateResult!.radioButtonText;

        if (showGlyph) {
            return {
                toggleButtonText: (config.accordionItemTitle && config.accordionItemTitle) || '',
                collapseProps: { isOpen: expanded },
                openGlyph: 'expand-glyph',
                closeGlyph: 'collapse-glyph',
                glyphPlacement: config && config.glyphPlacement,
                onToggle: this._onToggleAccordionItem(config.accordionItemTitle),
                radioButtonOption: config && config.radioButtonOption,
                radioButtonChecked: radioButtonText === config.accordionItemTitle ? true : false
            };
        }

        return {
            toggleButtonText: config.accordionItemTitle || '',
            collapseProps: { isOpen: this.expandSingleItemOnly ? radioButtonText === this.state.accordionItemTitle ? true : false : expanded },
            onToggle: this._onToggleAccordionItem(config.accordionItemTitle),
            radioButtonOption: config && config.radioButtonOption,
            radioButtonChecked: radioButtonText === config.accordionItemTitle ? true : false
        };
    }

    private _onToggleAccordionItem = (AccordionItemText: string, openOnly?: boolean) => (): void => {
        const data = this.props && this.props.data;
        const accordionExpandedStateResult = data && data.accordionExpandedState && data.accordionExpandedState.result;
        const radioButtonText = accordionExpandedStateResult!.radioButtonText;
        if (openOnly && (this.expandSingleItemOnly ? radioButtonText === this.state.accordionItemTitle ? true : false : this.state.expanded)) {
            return;
        }

        this.setState(prevState => ({
            expanded: !prevState.expanded
        }));

        if (this.expandSingleItemOnly) {
            const oldRadioButtonText = this.props.data && this.props.data.accordionExpandedState && this.props.data.accordionExpandedState.result!.radioButtonText;
            const closing = this.state.accordionItemTitle === oldRadioButtonText;
            const accordionState = { radioButtonText: closing ? '' : AccordionItemText, isAllExpanded: false };
            this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
        }
    }

    // private _closeAllAccordionItems = (AccordionItemText: string) => (): void => {
    //     if (this.expandSingleItemOnly) {
    //         const accordionState = { radioButtonText: AccordionItemText, isAllExpanded: false };
    //         this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
    //     }
    //     if (AccordionItemText !== this.state.accordionItemTitle) {
    //         this.setState({ accordionItemTitle: AccordionItemText });
    //     } else {
    //         this.setState({ accordionItemTitle: '' });
    //     }
    // }

    private _renderAccordionItemContent(content: string): JSX.Element | null {
        return (
            <div className='container'>
                <p>{content}</p>
            </div>
        );
    }

    private _renderAccordionItemSlotsContent(items: React.ReactNode[]): JSX.Element | null {
        return (
            <React.Fragment>
                {items && items.length ? (
                    items.map((slot: React.ReactNode, index: number) => {
                        return (
                            <React.Fragment key={index}>
                                {slot}
                            </React.Fragment>
                        );
                    })
                ) : null}
            </React.Fragment>
        );
    }
}

export default AccordionItemCustom;
